// D. Merchant governance: states, identification, and profile requests.
//
// The distinction the screen has to carry: ConfirmPH verifying that a
// business is who it says it is, versus ConfirmPH saying a business is
// legally compliant. Every state card repeats the second never happens.
import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";

import {
  CAP, MERCHANT_STATE_TONES, can, fmtDate, humanise, legalError, plGet, plPost,
} from "../../lib/platformLegal";
import {
  Button, Empty, Field, Loading, Modal, Note, Section, Stat, Table, TonePill, inputClass,
} from "./ui";

const ASSIGNABLE = ["OPERATIONAL", "RESTRICTED", "UNDER_REVIEW", "SUSPENDED", "REMOVED"];

export default function MerchantsSection({ capabilities }) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState({ state: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const writable = can(capabilities, CAP.merchants);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [merchants, profileRequests] = await Promise.all([
        plGet("/merchants", { params: { ...(filter.state ? { state: filter.state } : {}),
                                        ...(filter.q ? { q: filter.q } : {}) } }),
        plGet("/profile-requests"),
      ]);
      setRows(merchants.merchants);
      setSummary(merchants.summary);
      setRequests(profileRequests.requests);
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setLoading(false);
    }
  }, [filter.state, filter.q]);

  useEffect(() => { load(); }, [load]);

  const open = async (companyId) => {
    try {
      setDetail(await plGet(`/merchants/${companyId}`));
    } catch (err) {
      toast.error(legalError(err));
    }
  };

  if (loading && !rows.length) return <Loading label="Reading the merchant register..." />;

  return (
    <div className="space-y-4">
      {summary && (
        <Section testId="pl-merchant-states" title="Merchant states">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(summary.by_state).map(([state, count]) => (
              <Stat key={state} label={humanise(state)} value={count}
                    testId={`pl-merchant-state-${state}`} />
            ))}
          </div>
          <Note tone="slate" testId="pl-merchant-distinction">
            Business identity verified means ConfirmPH checked the business is who it says it is.
            It is never a statement that a business is legally compliant, licensed, insured, or
            that its vehicles are roadworthy — ConfirmPH does not assess any of that.
          </Note>
        </Section>
      )}

      <Section testId="pl-merchants"
               title="Businesses"
               actions={
                 <div className="flex flex-wrap items-center gap-2">
                   <select className={`${inputClass} w-auto`} value={filter.state}
                           onChange={(e) => setFilter({ ...filter, state: e.target.value })}
                           data-testid="pl-merchant-filter">
                     <option value="">All states</option>
                     {Object.keys(summary?.by_state || {}).map((state) => (
                       <option key={state} value={state}>{humanise(state)}</option>
                     ))}
                   </select>
                   <div className="flex items-center gap-1">
                     <input className={`${inputClass} w-40`} placeholder="Search"
                            value={filter.q}
                            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                            data-testid="pl-merchant-search" />
                     <Button variant="secondary" onClick={load} aria-label="Search">
                       <Search className="w-4 h-4" />
                     </Button>
                   </div>
                 </div>
               }>
        {rows.length === 0 ? (
          <Empty>No businesses match.</Empty>
        ) : (
          <Table head={["Business", "State", "Identity", "Can transact", "Badge eligible"]}
                 testId="pl-merchant-table" minWidth={640}>
            {rows.map((row) => (
              <tr key={row.company_id} data-testid={`pl-merchant-${row.company_id}`}>
                <td className="py-2 pr-3">
                  <button onClick={() => open(row.company_id)}
                          className="text-left font-medium text-slate-900 hover:underline">
                    {row.name}
                  </button>
                  <div className="text-xs text-slate-500">
                    {row.city || "—"}{row.is_demo ? " · demo" : ""}
                  </div>
                </td>
                <td className="py-2 pr-3">
                  <TonePill tone={MERCHANT_STATE_TONES[row.state]}>{humanise(row.state)}</TonePill>
                </td>
                <td className="py-2 pr-3 text-xs text-slate-600">
                  {humanise(row.identity_status)}
                  {row.reverification_due_at && (
                    <div className="text-slate-500">Due {fmtDate(row.reverification_due_at)}</div>
                  )}
                </td>
                <td className="py-2 pr-3 text-xs">{row.can_transact ? "Yes" : "No"}</td>
                <td className="py-2 pr-3 text-xs">{row.badge_eligible ? "Yes" : "No"}</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section testId="pl-profile-requests"
               title="Claim, correction and removal requests"
               description="Reviewed by a person. Knowing a business's name and email is not proof
                            of ownership, so nothing transfers automatically.">
        {requests.length === 0 ? (
          <Empty testId="pl-requests-empty">No open requests.</Empty>
        ) : (
          <Table head={["Reference", "Type", "Business", "Requester", "Status", "Actions"]}
                 testId="pl-request-table" minWidth={700}>
            {requests.map((request) => (
              <tr key={request.id} data-testid={`pl-request-${request.reference}`}>
                <td className="py-2 pr-3 font-mono text-xs">{request.reference}</td>
                <td className="py-2 pr-3 text-xs">{humanise(request.request_type)}</td>
                <td className="py-2 pr-3 text-xs">{request.company_name}</td>
                <td className="py-2 pr-3 text-xs">
                  {request.requester_name}
                  <div className="text-slate-500">{request.requester_email}</div>
                </td>
                <td className="py-2 pr-3 text-xs">{humanise(request.status)}</td>
                <td className="py-2 pr-3">
                  {writable && ["received", "under_review"].includes(request.status) && (
                    <DecideRequest request={request} onDone={load} />
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      {detail && (
        <MerchantModal detail={detail} writable={writable} onClose={() => setDetail(null)}
                       onChanged={async () => { await open(detail.state.company_id); await load(); }} />
      )}
    </div>
  );
}

function DecideRequest({ request, onDone }) {
  const [busy, setBusy] = useState(false);
  const decide = async (decision) => {
    const note = window.prompt(
      `Record what was checked and why this is the outcome (${decision}):`);
    if (!note) return;
    setBusy(true);
    try {
      await plPost(`/profile-requests/${request.id}/decide`,
                   { decision, note, apply_action: decision === "approved" });
      toast.success("Decision recorded.");
      onDone();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-1.5">
      <Button variant="secondary" busy={busy} onClick={() => decide("approved")}>Approve</Button>
      <Button variant="secondary" busy={busy} onClick={() => decide("declined")}>Decline</Button>
    </div>
  );
}

function MerchantModal({ detail, writable, onClose, onChanged }) {
  const { state, identity } = detail;
  const [form, setForm] = useState({ state: "UNDER_REVIEW", reason: "" });
  const [busy, setBusy] = useState(false);

  const setState = async () => {
    setBusy(true);
    try {
      await plPost(`/merchants/${state.company_id}/state`, form);
      toast.success("Merchant state recorded.");
      onChanged();
    } catch (err) {
      toast.error(legalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={identity?.legal_business_name || "Merchant"} onClose={onClose}
           testId="pl-merchant-modal" description={state.meaning}>
      <div className="flex flex-wrap items-center gap-2">
        <TonePill tone={MERCHANT_STATE_TONES[state.state]}>{humanise(state.state)}</TonePill>
        <span className="text-xs text-slate-600">{state.reason}</span>
      </div>
      <Note tone="slate">{state.note}</Note>

      <div className="rounded-xl border border-slate-200 p-3">
        <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          Merchant identification
        </div>
        {identity ? (
          <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-slate-500">Legal name</dt>
            <dd className="text-slate-800 sm:text-right">{identity.legal_business_name || "—"}</dd>
            <dt className="text-slate-500">Entity type</dt>
            <dd className="text-slate-800 sm:text-right">{humanise(identity.entity_type) || "—"}</dd>
            <dt className="text-slate-500">Representative</dt>
            <dd className="text-slate-800 sm:text-right">{identity.representative_name || "—"}</dd>
            <dt className="text-slate-500">Registration no.</dt>
            <dd className="text-slate-800 sm:text-right">{identity.registration_number || "—"}</dd>
            <dt className="text-slate-500">Status</dt>
            <dd className="text-slate-800 sm:text-right">{humanise(identity.status)}</dd>
            <dt className="text-slate-500">Reverification due</dt>
            <dd className="text-slate-800 sm:text-right">
              {fmtDate(identity.reverification_due_at)}
            </dd>
          </dl>
        ) : (
          <Empty>No identification record has been started.</Empty>
        )}
      </div>

      {writable && (
        <div className="rounded-xl border border-slate-200 p-3 space-y-3">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Record a platform decision
          </div>
          <Field label="State">
            <select className={inputClass} value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    data-testid="pl-merchant-state-select">
              {ASSIGNABLE.map((s) => <option key={s} value={s}>{humanise(s)}</option>)}
            </select>
          </Field>
          <Field label="Reason" hint="This restricts a real business: say why.">
            <textarea className={inputClass} rows={3} value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      data-testid="pl-merchant-reason" />
          </Field>
          <div className="flex justify-end">
            <Button onClick={setState} busy={busy} data-testid="pl-merchant-save">
              Record decision
            </Button>
          </div>
        </div>
      )}

      {detail.events?.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-3">
          <div className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Decision history
          </div>
          <ul className="mt-2 space-y-1.5 text-xs">
            {detail.events.map((event) => (
              <li key={event.id} className="text-slate-700">
                <span className="font-medium">{humanise(event.from_state)} →{" "}
                  {humanise(event.to_state)}</span>{" "}
                <span className="text-slate-500">
                  {fmtDate(event.decided_at)} · {event.decided_by_name}
                </span>
                <div className="text-slate-600">{event.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Modal>
  );
}
